import { ButtonLink, DetailsListItem, Grid, ListContentIsland } from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { Route } from '../../../routes/account.members_.$customerId';
import { MainLayout } from '../../Layout/MainLayout';
import { useBusinessMemberQuery } from '../../../api/members';
import { CardList } from '../HomePage/CardList';
import { MainBreadcrumbs } from '../../Layout/MainBreadcrumbs';
import { AccountList } from '../HomePage/AccountList';
import { useAccountsQuery } from '../../../api/accounts';
import { useCardListQuery } from '../../../api/cards';
import { useMemo } from 'react';
import { useIsCardsAvailable } from '../../../api/customers';
import { fullName } from '@razz-team/utils';
import { useHasRole } from '../../../api/access';
import { getMemberDisplayName, getMemberEmail } from '../BusinessPage/Member/utils';

export function MemberPage() {
  const { t } = useTranslation();
  const { customerId } = Route.useParams();
  const isAdmin = useHasRole('ADMIN');
  const isCardsAvailable = useIsCardsAvailable();
  const memberQuery = useBusinessMemberQuery({ customerId });
  const accountsQuery = useAccountsQuery();
  const cardsQuery = useCardListQuery();

  const cards = useMemo(() => {
    return cardsQuery.data?.filter((it) => it.cardHolderId === customerId);
  }, [cardsQuery.data, customerId]);

  const accounts = useMemo(() => {
    return accountsQuery.data?.filter((account) =>
      cards?.some((card) => card.spendingStrategy.accountId === account.id),
    );
  }, [accountsQuery.data, cards]);

  const memberName =
    getMemberDisplayName(memberQuery.data) ?? getMemberEmail(memberQuery.data) ?? t('Member');

  const isEditable =
    isAdmin &&
    memberQuery.data?.individual.state !== 'VERIFIED' &&
    memberQuery.data?.membership.role !== 'OWNER';

  return (
    <MainLayout
      title={
        <MainBreadcrumbs
          title={memberName}
          loading={memberQuery.isPending}
          parent={{
            to: '/account/members',
            title: t('Linked members'),
          }}
        />
      }
      compact
    >
      <Grid size={12}>
        <ListContentIsland
          title={t('Member information')}
          actions={
            isEditable ? (
              <ButtonLink
                to="/account/members/$customerId/edit"
                params={{ customerId }}
                variant="text"
              >
                {t('Edit')}
              </ButtonLink>
            ) : null
          }
        >
          <DetailsListItem
            label={t('Name')}
            value={fullName(memberQuery.data?.individual.name ?? {})}
            loading={memberQuery.isPending}
          />
          <DetailsListItem
            label={t('Email')}
            value={getMemberEmail(memberQuery.data)}
            loading={memberQuery.isPending}
          />
          <DetailsListItem
            label={t('Phone')}
            value={memberQuery.data?.identity.phone ?? t('Not set')}
            loading={memberQuery.isPending}
          />
        </ListContentIsland>
      </Grid>
      {isCardsAvailable ? (
        <Grid size={{ xs: 12, md: 6 }}>
          <CardList
            title={t('Member cards')}
            cards={cards}
            error={cardsQuery.error}
            isLoading={cardsQuery.isPending}
          />
        </Grid>
      ) : null}
      <Grid size={{ xs: 12, md: 6 }}>
        <AccountList
          title={t('Member accounts')}
          accounts={accounts}
          error={accountsQuery.error}
          isLoading={accountsQuery.isPending}
        />
      </Grid>
    </MainLayout>
  );
}
